import React, {
  ChangeEvent,
  FC,
  ReactElement,
  useEffect,
  useState,
} from 'react'
import SelectieButtonEnum from '../SelectieButtonEnum'
import {
  DaysOfWeek,
  KoppelingType,
  VestigingEnum,
  WareHouseSearchOptions,
  daysOfWeekOptions,
  koppelingTypeOptions,
  vestigingen,
  warehouseOptions,
} from 'shared/models/Enum'
import {
  IClearDataResponseAction,
  IGetLocationsResponseAction,
} from 'redux/store/actions/glnLocatieZoekAction'
import { IGlnLocatieZoekSelectie } from 'shared/models/IGlnLocatieZoekSelectie'
import Cookies from 'universal-cookie'
import { LocationLinkCookie } from 'shared/Constants'

interface ISearchBoxProps {
  searchLocations: (
    selectie: IGlnLocatieZoekSelectie
  ) => Promise<IGetLocationsResponseAction>
  clearData: (message: string) => Promise<IClearDataResponseAction>
}

enum SearchBoxType {
  CustomerNumber,
  CustomerName,
  LocationNumber,
  LocationName,
  All,
}

const CustomerLocationLinkSearchBox: FC<ISearchBoxProps> = (
  props: ISearchBoxProps
): ReactElement => {
  const [warehouse, setWarehouse] = useState<WareHouseSearchOptions>(
    WareHouseSearchOptions.Alle
  )
  const [daysOfWeek, setDaysOfWeek] = useState<DaysOfWeek>(DaysOfWeek.Alle)
  const [koppelingType, setKoppelingType] = useState<KoppelingType>(
    KoppelingType.Alle
  )
  const [locationName, setLocationName] = useState<string>('')
  const [locationNumber, setLocationNumber] = useState<string>('')
  const [customerNumber, setCustomerNumber] = useState<string>('')
  const [customerName, setCustomerName] = useState<string>('')
  const [deliveryAgreement, setDeliveryAgreement] = useState<string>('')

  useEffect(() => {
    const warehouse = Number(getVestigingCookieForPage())
    setWarehouse(warehouseOptions[warehouse])
  }, [])

  const checkIfSearchFieldsAreFilled = (): boolean => {
    const fieldsAreEmpty =
      locationName === '' &&
      locationNumber === '' &&
      customerNumber === '' &&
      customerName === '' &&
      deliveryAgreement === ''

    if (fieldsAreEmpty) {
      props.clearData('Vul aub 1 van de zoekvelden in.')
      return false
    }
    return true
  }

  const onSearchLocations = async () => {
    if (!checkIfSearchFieldsAreFilled()) {
      return
    }

    const searchSelection: IGlnLocatieZoekSelectie = {
      afleverAfspraakReferentie: deliveryAgreement,
      klantNaam: customerName,
      klantNummer: Number(customerNumber),
      locatieGln: Number(locationNumber),
      locatieNaam: locationName,
      vestiging: warehouse,
      weekDag: daysOfWeek,
      koppelingType: koppelingType,
    }

    console.log(searchSelection)
    await props.searchLocations(searchSelection)
  }

  const clearInputforSearchBoxes = (
    searchNumberType: SearchBoxType = SearchBoxType.All
  ) => {
    switch (searchNumberType) {
      case SearchBoxType.CustomerNumber:
        setCustomerName('')
        setLocationNumber('')
        setLocationName('')
        break
      case SearchBoxType.CustomerName:
        setCustomerNumber('')
        setLocationNumber('')
        setLocationName('')
        break
      case SearchBoxType.LocationNumber:
        setCustomerNumber('')
        setCustomerName('')
        setLocationName('')
        break
      case SearchBoxType.LocationName:
        setCustomerNumber('')
        setCustomerName('')
        setLocationNumber('')
        break
      case SearchBoxType.All:
      default:
        setCustomerNumber('')
        setCustomerName('')
        setLocationNumber('')
        setLocationName('')
        setDeliveryAgreement('')
    }
    setDaysOfWeek(DaysOfWeek.Alle)
    setKoppelingType(KoppelingType.Alle)
  }

  const zoekIfEnter = (e: string) => {
    if (e === 'Enter') {
      onSearchLocations()
    }
  }

  const updateWarehouse = (eventParam: ChangeEvent<HTMLSelectElement>) => {
    const warehouseNumber = Number(eventParam.target.value)
    if (Number.isNaN(warehouseNumber)) {
      return
    }
    const chosenWarehousEnum: WareHouseSearchOptions =
      getWarehouseSearchOption(warehouseNumber)
    setVestigingCookieForPage(vestigingen[Number(chosenWarehousEnum)])
    setWarehouse(chosenWarehousEnum)
  }

  const updateDaysOfWeek = (eventParam: ChangeEvent<HTMLSelectElement>) => {
    const daysOfWeekNumber = Number(eventParam.target.value)
    if (Number.isNaN(daysOfWeekNumber)) {
      return
    }
    setDaysOfWeek(daysOfWeekOptions[daysOfWeekNumber])
  }

  const updateKoppelingType = (eventParam: ChangeEvent<HTMLSelectElement>) => {
    const koppelingTypeNumber = Number(eventParam.target.value)
    if (Number.isNaN(koppelingTypeNumber)) {
      return
    }
    setKoppelingType(koppelingTypeOptions[koppelingTypeNumber])
  }

  const getWarehouseSearchOption = (
    warehouseNumber: number
  ): WareHouseSearchOptions => {
    return warehouseOptions.find(wso => wso == warehouseNumber)
  }

  const getVestigingCookieForPage = (): WareHouseSearchOptions => {
    const cookies = new Cookies()
    const cookieValue = cookies.get(LocationLinkCookie.cookieName)
    const cookieValueAsNumber = Number(cookieValue)
    if (Number.isNaN(cookieValueAsNumber)) {
      return WareHouseSearchOptions.Alle
    }
    return getWarehouseSearchOption(cookieValueAsNumber)
  }

  const setVestigingCookieForPage = (i: VestigingEnum) => {
    if (i > 0) {
      const cookies = new Cookies()
      cookies.set(LocationLinkCookie.cookieName, i.toString(), {
        path: LocationLinkCookie.cookiePath,
        maxAge: LocationLinkCookie.cookieMaxAge,
      })
    }
  }

  const setNumericField = (
    event: React.ChangeEvent<HTMLInputElement>,
    searchNumberType: SearchBoxType
  ) => {
    //Check if the event value is a number.
    //Input type "number" thinks e is a valid number
    //See https://stackoverflow.com/a/31706796
    const valueAsNumber: number = Number(event.target.value)
    if (Number.isNaN(valueAsNumber)) {
      return
    }
    switch (searchNumberType) {
      case SearchBoxType.CustomerNumber:
        setCustomerNumber(event.target.value)
        break
      case SearchBoxType.LocationNumber:
        setLocationNumber(event.target.value)
        break
    }
  }

  return (
    <>
      <table className='selectie'>
        <thead>
          <tr>
            <th className='selectie-label'>Klantnummer (CKN):</th>
            <th className='selectie-label'>Klantnaam:</th>
            <th className='selectie-label'>Afleverafspraakreferentie:</th>
            <th className='selectie-label'>Locatie (GLN):</th>
            <th className='selectie-label'>Locatienaam (Route-to/Ship-to):</th>
            <th className='selectie-label'>Weekdag:</th>
            <th className='selectie-label'>Type:</th>
            <th className='selectie-label'>Vestiging:</th>
          </tr>
        </thead>
        <tbody>
          <tr onKeyUp={e => zoekIfEnter(e.key)}>
            <td>
              <input
                type='tel'
                value={customerNumber}
                placeholder='Klantnummer (CKN)'
                onKeyDown={e =>
                  clearInputforSearchBoxes(SearchBoxType.CustomerNumber)
                }
                onChange={e => setNumericField(e, SearchBoxType.CustomerNumber)}
              />
            </td>
            <td>
              <input
                value={customerName}
                placeholder='Klantnaam'
                onKeyDown={e =>
                  clearInputforSearchBoxes(SearchBoxType.CustomerName)
                }
                onChange={e => setCustomerName(e.target.value)}
              />
            </td>
            <td>
              <input
                value={deliveryAgreement}
                placeholder='Afleverafspraakreferentie'
                onChange={e => setDeliveryAgreement(e.target.value)}
              />
            </td>
            <td>
              <input
                type='tel'
                value={locationNumber}
                placeholder='Locatie (GLN)'
                onKeyDown={e =>
                  clearInputforSearchBoxes(SearchBoxType.LocationNumber)
                }
                onChange={e => setNumericField(e, SearchBoxType.LocationNumber)}
              />
            </td>
            <td>
              <input
                className='w-100'
                placeholder='Locatienaam (Route-to/Ship-to)'
                onKeyDown={e =>
                  clearInputforSearchBoxes(SearchBoxType.LocationName)
                }
                value={locationName}
                onChange={e => setLocationName(e.target.value)}
              />
            </td>
            <td>
              <SelectieButtonEnum
                type={DaysOfWeek}
                options={daysOfWeekOptions}
                selected={daysOfWeek}
                onChange={updateDaysOfWeek}
              />
            </td>
            <td>
              <SelectieButtonEnum
                type={KoppelingType}
                options={koppelingTypeOptions}
                selected={koppelingType}
                onChange={updateKoppelingType}
              />
            </td>
            <td>
              <SelectieButtonEnum
                type={WareHouseSearchOptions}
                options={warehouseOptions}
                selected={warehouse}
                onChange={updateWarehouse}
              />
            </td>
          </tr>
          <tr>
            <td colSpan={8}>
              <div className='row no-gutters'>
                <div className='col'></div>
                <div className='col-auto'>
                  <button
                    className='mr-2 btn btn-secondary'
                    onClick={e => clearInputforSearchBoxes()}
                  >
                    Wissen
                  </button>
                  <button
                    className='btn btn-primary'
                    onClick={e => onSearchLocations()}
                  >
                    Zoek Locatie
                  </button>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  )
}

export default CustomerLocationLinkSearchBox
